import React, { useEffect, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import CwFPagination from 'components/CwFPagination';
import { Table } from 'syngenta-digital-cropwise-react-ui-kit';
import LoadingSpinner from 'screens/Integrations/components/IntegrationSpinner';
import 'styles/uiKitTableReset.css';
import 'screens/Integrations/components/integrationTable.css';

import { SubmissionHistoryContext } from './context/Provider';
import { DATE_RANGE_COLUMN, SUBMISSIONS_HISTORY_TABLE } from './constants';
import TableRow from './components/TableRow';

import './index.css';
import { INTEGRATIONS } from '../helpers/constants';

const SubmissionHistory = ({ vendor = 'not specified' }) => {
  const {
    getData,
    loading,
    state: { data: submissions, elements, page, limit }
  } = useContext(SubmissionHistoryContext);

  const [sortFields, setSortFields] = useState({
    sortBy: 'date',
    sortDir: 'desc'
  });

  const columns = useMemo(() => {
    if (vendor === INTEGRATIONS.calAgPermits) {
      const newColumn = [...SUBMISSIONS_HISTORY_TABLE.columns];
      newColumn.splice(4, 0, DATE_RANGE_COLUMN);
      return newColumn;
    }
    return SUBMISSIONS_HISTORY_TABLE.columns;
  }, [vendor]);

  const handleSort = async (sortBy, sortDir) => {
    if (loading) {
      return;
    }

    await getData(sortDir, sortBy === 'date' ? 'created_at' : sortBy);
    setSortFields({
      sortBy,
      sortDir
    });
  };

  const handlePageChange = async nextPage => {
    if (loading) {
      return;
    }
    await getData(
      sortFields.sortDir,
      sortFields.sortBy === 'date' ? 'created_at' : sortFields.sortBy,
      nextPage
    );
  };

  const sortPaths = {
    asc:
      'M8.5575 14.7802L12 11.3452L15.4425 14.7802L16.5 13.7227L12 9.22266L7.5 13.7227L8.5575 14.7802Z',
    desc:
      'M8.5575 9.22266L12 12.6577L15.4425 9.22266L16.5 10.2802L12 14.7802L7.5 10.2802L8.5575 9.22266Z',
    none:
      'M14.3776 9.75L12.0001 7.3725L9.62262 9.75L8.55762 8.6925L12.0001 5.25L15.4351 8.6925L14.3776 9.75ZM9.62262 14.25L12.0001 16.6275L14.3776 14.25L15.4426 15.3075L12.0001 18.75L8.56512 15.3075L9.62262 14.25Z'
  };

  const tableColumns = columns.map(({ field, title, width, sortable }) => ({
    key: field || 'actions',
    title: sortable ? (
      <span className="flex items-center h-5">
        {title}
        <svg
          className="shrink-0 ml-1"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            fill="#696F88"
            d={
              sortPaths[
                sortFields.sortBy === field ? sortFields.sortDir : 'none'
              ]
            }
          />
        </svg>
      </span>
    ) : (
      title
    ),
    onHeaderCell: () => ({ style: { width } }),
    sorter: sortable,
    sortOrder:
      sortable && sortFields.sortBy === field
        ? `${sortFields.sortDir}end`
        : null,
    render: (_, submission) => (
      <TableRow field={field || 'actions'} submission={submission} />
    )
  }));

  const handleTableChange = async (_, __, sorter) => {
    const field = sorter.columnKey;
    if (field) {
      await handleSort(
        field,
        sortFields.sortBy !== field || sortFields.sortDir === 'asc'
          ? 'desc'
          : 'asc'
      );
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-void -- useSubmission handles request errors.
    void getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      {submissions?.length > 0 && (
        <div className="mb-6">
          <div className="w-full mb-2 font-semibold">
            <p className="text-xl">Previous Submissions</p>
          </div>
          <div
            className={`flex flex-col bg-white justify-between w-full integration-submission-history ${
              loading ? 'previous-submission-table-loading' : ''
            }`}
          >
            <div
              className="cwf-table-flush cwf-table-no-row-hover integration-table"
              data-testid="submission-history-table"
            >
              <Table
                rowKey="id"
                columns={tableColumns}
                dataSource={loading ? [] : submissions}
                pagination={false}
                showSorterTooltip={false}
                locale={{ emptyText: null }}
                onChange={handleTableChange}
              />
            </div>
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <LoadingSpinner
                  message=""
                  size={75}
                  label="Loading submissions"
                />
              </div>
            )}
            <div
              className={`w-full pr-2 ${
                loading ? 'pointer-events-none cursor-not-allowed' : ''
              }`}
            >
              {CwFPagination(elements, page, handlePageChange, null, limit)}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

SubmissionHistory.propTypes = {
  vendor: PropTypes.string
};

export default SubmissionHistory;
